const logger = {
  error: (...args: unknown[]) => {
    if (typeof globalThis !== "undefined" && globalThis.console) {
      globalThis.console["error"](...args);
    }
  },
};

// ===========================================
// PWA CONFIGURATION ENGINE
// Complete Progressive Web App management with
// manifest settings, install prompts, push notifications,
// service worker control, and analytics
// ===========================================

import * as React from "react";

// ===========================================
// TYPES & INTERFACES
// ===========================================

export type PWADisplayMode = "fullscreen" | "standalone" | "minimal-ui" | "browser";
export type PWAOrientation = "any" | "natural" | "landscape" | "portrait" | "portrait-primary" | "portrait-secondary" | "landscape-primary" | "landscape-secondary";
export type NotificationPermission = "default" | "granted" | "denied";
export type InstallState = "idle" | "available" | "installing" | "installed" | "dismissed";

export interface PWAManifestConfig {
  name: string;
  shortName: string;
  description: string;
  themeColor: string;
  backgroundColor: string;
  display: PWADisplayMode;
  orientation: PWAOrientation;
  startUrl: string;
  scope: string;
  lang: string;
  dir: "ltr" | "rtl" | "auto";
  categories: string[];
  icons: PWAIcon[];
  screenshots: PWAScreenshot[];
  shortcuts: PWAShortcut[];
  relatedApplications: PWARelatedApp[];
  preferRelatedApplications: boolean;
}

export interface PWAIcon {
  src: string;
  sizes: string;
  type: string;
  purpose?: "any" | "maskable" | "monochrome" | "any maskable";
}

export interface PWAScreenshot {
  src: string;
  sizes: string;
  type: string;
  label?: string;
  platform?: "narrow" | "wide";
  formFactor?: "narrow" | "wide";
}

export interface PWAShortcut {
  name: string;
  shortName?: string;
  description?: string;
  url: string;
  icons?: PWAIcon[];
}

export interface PWARelatedApp {
  platform: "play" | "itunes" | "windows" | "webapp";
  url: string;
  id?: string;
}

export interface PushNotificationConfig {
  enabled: boolean;
  vapidPublicKey: string;
  topics: NotificationTopic[];
  quietHoursEnabled: boolean;
  quietHoursStart: string; // HH:MM format
  quietHoursEnd: string;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  badgeEnabled: boolean;
}

export interface NotificationTopic {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  priority: "high" | "normal" | "low";
}

export interface PWASettings {
  manifestConfig: PWAManifestConfig;
  pushConfig: PushNotificationConfig;
  installPromptEnabled: boolean;
  installPromptDelay: number; // milliseconds before showing prompt
  installPromptFrequency: number; // days between prompts
  offlinePageEnabled: boolean;
  splashScreenEnabled: boolean;
  analyticsEnabled: boolean;
  autoUpdateEnabled: boolean;
  updatePromptEnabled: boolean;
}

export interface PWAAnalytics {
  totalInstalls: number;
  activeInstalls: number;
  installsByPlatform: Record<string, number>;
  installsByDate: Array<{ date: string; count: number }>;
  uninstalls: number;
  pushSubscribers: number;
  pushDelivered: number;
  pushClicked: number;
  pushDismissed: number;
  pushClickRate: number;
  offlineUsage: number;
  averageSessionDuration: number;
  launchesFromHomeScreen: number;
}

export interface PWAState {
  isInstallable: boolean;
  installState: InstallState;
  isStandalone: boolean;
  notificationPermission: NotificationPermission;
  isPushSubscribed: boolean;
  serviceWorkerStatus: "pending" | "installing" | "waiting" | "active" | "error" | "unsupported";
  lastUpdateCheck: string | null;
  updateAvailable: boolean;
  isOnline: boolean;
}

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

// ===========================================
// CONSTANTS
// ===========================================

const STORAGE_KEYS = {
  PWA_SETTINGS: "showcase_pwa_settings",
  PWA_STATE: "showcase_pwa_state",
  PUSH_SUBSCRIPTION: "showcase_push_subscription",
  INSTALL_DISMISSED_AT: "showcase_install_dismissed_at",
  PWA_ANALYTICS: "showcase_pwa_analytics",
} as const;

const DEFAULT_MANIFEST: PWAManifestConfig = {
  name: "Showbold - AI Marketing Platform",
  shortName: "Showbold",
  description: "AI-Powered Social Media Marketing Platform",
  themeColor: "#8b5cf6",
  backgroundColor: "#0f172a",
  display: "standalone",
  orientation: "any",
  startUrl: "/dashboard",
  scope: "/",
  lang: "en",
  dir: "ltr",
  categories: ["business", "productivity", "social"],
  icons: [
    { src: "/icons/icon-72x72.png", sizes: "72x72", type: "image/png", purpose: "any" },
    { src: "/icons/icon-96x96.png", sizes: "96x96", type: "image/png", purpose: "any" },
    { src: "/icons/icon-128x128.png", sizes: "128x128", type: "image/png", purpose: "any" },
    { src: "/icons/icon-144x144.png", sizes: "144x144", type: "image/png", purpose: "any" },
    { src: "/icons/icon-152x152.png", sizes: "152x152", type: "image/png", purpose: "any" },
    { src: "/icons/icon-192x192.png", sizes: "192x192", type: "image/png", purpose: "any" },
    { src: "/icons/icon-384x384.png", sizes: "384x384", type: "image/png", purpose: "any" },
    { src: "/icons/icon-512x512.png", sizes: "512x512", type: "image/png", purpose: "any" },
    { src: "/icons/icon-maskable-192x192.png", sizes: "192x192", type: "image/png", purpose: "maskable" },
    { src: "/icons/icon-maskable-512x512.png", sizes: "512x512", type: "image/png", purpose: "maskable" },
  ],
  screenshots: [
    { src: "/screenshots/desktop-dashboard.png", sizes: "1920x1080", type: "image/png", label: "Dashboard", formFactor: "wide" },
    { src: "/screenshots/desktop-analytics.png", sizes: "1920x1080", type: "image/png", label: "Analytics", formFactor: "wide" },
    { src: "/screenshots/mobile-dashboard.png", sizes: "750x1334", type: "image/png", label: "Mobile Dashboard", formFactor: "narrow" },
    { src: "/screenshots/mobile-composer.png", sizes: "750x1334", type: "image/png", label: "Post Composer", formFactor: "narrow" },
  ],
  shortcuts: [
    { name: "New Post", shortName: "Post", description: "Create a new social media post", url: "/dashboard/compose?quick=true" },
    { name: "Analytics", shortName: "Stats", description: "View your analytics", url: "/dashboard/analytics" },
    { name: "Calendar", shortName: "Schedule", description: "View content calendar", url: "/dashboard/calendar" },
    { name: "Settings", shortName: "Settings", description: "Manage your account", url: "/dashboard/settings" },
  ],
  relatedApplications: [],
  preferRelatedApplications: false,
};

const DEFAULT_PUSH_CONFIG: PushNotificationConfig = {
  enabled: true,
  vapidPublicKey: "",
  topics: [
    { id: "posts", name: "Post Activity", description: "When your posts get engagement", enabled: true, icon: "📱", priority: "normal" },
    { id: "followers", name: "New Followers", description: "When you gain new followers", enabled: true, icon: "👥", priority: "normal" },
    { id: "milestones", name: "Milestones", description: "Achievement notifications", enabled: true, icon: "🎉", priority: "high" },
    { id: "scheduled", name: "Scheduled Posts", description: "Reminders for scheduled content", enabled: true, icon: "📅", priority: "normal" },
    { id: "trends", name: "Trending Topics", description: "Relevant trends in your niche", enabled: false, icon: "📈", priority: "low" },
    { id: "ai", name: "AI Suggestions", description: "Smart content recommendations", enabled: false, icon: "🤖", priority: "low" },
    { id: "team", name: "Team Activity", description: "Updates from team members", enabled: true, icon: "👋", priority: "normal" },
    { id: "system", name: "System Updates", description: "Important platform updates", enabled: true, icon: "⚙️", priority: "high" },
  ],
  quietHoursEnabled: true,
  quietHoursStart: "22:00",
  quietHoursEnd: "08:00",
  soundEnabled: true,
  vibrationEnabled: true,
  badgeEnabled: true,
};

const DEFAULT_SETTINGS: PWASettings = {
  manifestConfig: DEFAULT_MANIFEST,
  pushConfig: DEFAULT_PUSH_CONFIG,
  installPromptEnabled: true,
  installPromptDelay: 30000, // 30 seconds
  installPromptFrequency: 7, // 7 days
  offlinePageEnabled: true,
  splashScreenEnabled: true,
  analyticsEnabled: true,
  autoUpdateEnabled: true,
  updatePromptEnabled: true,
};

const DEFAULT_ANALYTICS: PWAAnalytics = {
  totalInstalls: 0,
  activeInstalls: 0,
  installsByPlatform: {},
  installsByDate: [],
  uninstalls: 0,
  pushSubscribers: 0,
  pushDelivered: 0,
  pushClicked: 0,
  pushDismissed: 0,
  pushClickRate: 0,
  offlineUsage: 0,
  averageSessionDuration: 0,
  launchesFromHomeScreen: 0,
};

// ===========================================
// STORAGE UTILITIES
// ===========================================

const storage = {
  get<T>(key: string): T | null {
    if (typeof window === "undefined") return null;
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      logger.error(`Failed to read ${key} from storage`);
      return null;
    }
  },

  set<T>(key: string, value: T): boolean {
    if (typeof window === "undefined") return false;
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      logger.error(`Failed to write ${key} to storage`);
      return false;
    }
  },

  remove(key: string): boolean {
    if (typeof window === "undefined") return false;
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },
};

// ===========================================
// PWA STATE MANAGER
// ===========================================

class PWAStateManager {
  private state: PWAState;
  private listeners: Set<(state: PWAState) => void>;
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private swRegistration: ServiceWorkerRegistration | null = null;

  constructor() {
    this.state = this.getInitialState();
    this.listeners = new Set();
    this.initializeListeners();
  }

  private getInitialState(): PWAState {
    const saved = storage.get<PWAState>(STORAGE_KEYS.PWA_STATE);
    return {
      isInstallable: false,
      installState: saved?.installState || "idle",
      isStandalone: this.checkStandalone(),
      notificationPermission: this.getNotificationPermission(),
      isPushSubscribed: false,
      serviceWorkerStatus: this.checkServiceWorkerSupport() ? "pending" : "unsupported",
      lastUpdateCheck: saved?.lastUpdateCheck || null,
      updateAvailable: false,
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    };
  }

  private checkStandalone(): boolean {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
      document.referrer.includes("android-app://")
    );
  }

  private getNotificationPermission(): NotificationPermission {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return "denied";
    }
    return Notification.permission as NotificationPermission;
  }

  private checkServiceWorkerSupport(): boolean {
    return typeof window !== "undefined" && "serviceWorker" in navigator;
  }

  private initializeListeners(): void {
    if (typeof window === "undefined") return;

    // Listen for install prompt
    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.updateState({ isInstallable: true, installState: "available" });
    });

    // Listen for app installed
    window.addEventListener("appinstalled", () => {
      this.deferredPrompt = null;
      this.updateState({ isInstallable: false, installState: "installed" });
      this.trackInstall();
    });

    // Listen for online/offline
    window.addEventListener("online", () => this.updateState({ isOnline: true }));
    window.addEventListener("offline", () => this.updateState({ isOnline: false }));

    // Listen for display mode changes
    window.matchMedia("(display-mode: standalone)").addEventListener("change", (e) => {
      this.updateState({ isStandalone: e.matches });
    });

    // Initialize service worker
    this.initializeServiceWorker();
  }

  private async initializeServiceWorker(): Promise<void> {
    if (!this.checkServiceWorkerSupport()) return;

    try {
      this.swRegistration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
      });

      // Update status based on SW state
      if (this.swRegistration.installing) {
        this.updateState({ serviceWorkerStatus: "installing" });
      } else if (this.swRegistration.waiting) {
        this.updateState({ serviceWorkerStatus: "waiting", updateAvailable: true });
      } else if (this.swRegistration.active) {
        this.updateState({ serviceWorkerStatus: "active" });
      }

      // Listen for updates
      this.swRegistration.addEventListener("updatefound", () => {
        const newWorker = this.swRegistration?.installing;
        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
              this.updateState({ updateAvailable: true, serviceWorkerStatus: "waiting" });
            }
          });
        }
      });

      // Check push subscription
      this.checkPushSubscription();
    } catch (error) {
      logger.error("Service worker registration failed:", error instanceof Error ? error : new Error(String(error)));
      this.updateState({ serviceWorkerStatus: "error" });
    }
  }

  private async checkPushSubscription(): Promise<void> {
    if (!this.swRegistration) return;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      this.updateState({ isPushSubscribed: !!subscription });
    } catch {
      // Push not supported
    }
  }

  private trackInstall(): void {
    const analytics = getPWAAnalytics();
    analytics.totalInstalls++;
    analytics.activeInstalls++;
    
    // Track platform
    const platform = this.detectPlatform();
    analytics.installsByPlatform[platform] = (analytics.installsByPlatform[platform] || 0) + 1;
    
    // Track by date
    const today = new Date().toISOString().split("T")[0];
    const dateEntry = analytics.installsByDate.find((d) => d.date === today);
    if (dateEntry) {
      dateEntry.count++;
    } else {
      analytics.installsByDate.push({ date: today, count: 1 });
    }
    
    storage.set(STORAGE_KEYS.PWA_ANALYTICS, analytics);
  }

  private detectPlatform(): string {
    if (typeof window === "undefined") return "unknown";
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return "ios";
    if (/android/.test(ua)) return "android";
    if (/windows/.test(ua)) return "windows";
    if (/macintosh|mac os x/.test(ua)) return "macos";
    if (/linux/.test(ua)) return "linux";
    return "other";
  }

  public updateState(partial: Partial<PWAState>): void {
    this.state = { ...this.state, ...partial };
    storage.set(STORAGE_KEYS.PWA_STATE, this.state);
    this.notifyListeners();
  }

  public getState(): PWAState {
    return { ...this.state };
  }

  public getDeferredPrompt(): BeforeInstallPromptEvent | null {
    return this.deferredPrompt;
  }

  public getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
    return this.swRegistration;
  }

  public subscribe(listener: (state: PWAState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.getState()));
  }

  public async promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
    if (!this.deferredPrompt) return "unavailable";

    this.updateState({ installState: "installing" });
    
    try {
      await this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        this.updateState({ installState: "installed" });
      } else {
        this.updateState({ installState: "dismissed" });
        storage.set(STORAGE_KEYS.INSTALL_DISMISSED_AT, new Date().toISOString());
      }
      
      this.deferredPrompt = null;
      return outcome;
    } catch {
      this.updateState({ installState: "available" });
      return "unavailable";
    }
  }

  public async subscribeToPush(vapidPublicKey: string): Promise<PushSubscription | null> {
    if (!this.swRegistration) return null;

    try {
      const subscription = await this.swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource,
      });

      this.updateState({ isPushSubscribed: true });
      storage.set(STORAGE_KEYS.PUSH_SUBSCRIPTION, subscription.toJSON());
      
      // Update analytics
      const analytics = getPWAAnalytics();
      analytics.pushSubscribers++;
      storage.set(STORAGE_KEYS.PWA_ANALYTICS, analytics);

      return subscription;
    } catch (error) {
      logger.error("Push subscription failed:", error instanceof Error ? error : new Error(String(error)));
      return null;
    }
  }

  public async unsubscribeFromPush(): Promise<boolean> {
    if (!this.swRegistration) return false;

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        this.updateState({ isPushSubscribed: false });
        storage.remove(STORAGE_KEYS.PUSH_SUBSCRIPTION);
        
        // Update analytics
        const analytics = getPWAAnalytics();
        analytics.pushSubscribers = Math.max(0, analytics.pushSubscribers - 1);
        storage.set(STORAGE_KEYS.PWA_ANALYTICS, analytics);
        
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  public async requestNotificationPermission(): Promise<NotificationPermission> {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return "denied";
    }

    const permission = await Notification.requestPermission();
    this.updateState({ notificationPermission: permission as NotificationPermission });
    return permission as NotificationPermission;
  }

  public async checkForUpdates(): Promise<boolean> {
    if (!this.swRegistration) return false;

    try {
      await this.swRegistration.update();
      this.updateState({ lastUpdateCheck: new Date().toISOString() });
      return true;
    } catch {
      return false;
    }
  }

  public async applyUpdate(): Promise<void> {
    if (!this.swRegistration?.waiting) return;

    this.swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
    
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      window.location.reload();
    });
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// ===========================================
// SETTINGS FUNCTIONS
// ===========================================

export function getPWASettings(): PWASettings {
  const saved = storage.get<PWASettings>(STORAGE_KEYS.PWA_SETTINGS);
  return { ...DEFAULT_SETTINGS, ...saved };
}

export function updatePWASettings(updates: Partial<PWASettings>): PWASettings {
  const current = getPWASettings();
  const updated = { ...current, ...updates };
  storage.set(STORAGE_KEYS.PWA_SETTINGS, updated);
  return updated;
}

export function updateManifestConfig(updates: Partial<PWAManifestConfig>): PWAManifestConfig {
  const settings = getPWASettings();
  const updated = { ...settings.manifestConfig, ...updates };
  updatePWASettings({ manifestConfig: updated });
  return updated;
}

export function updatePushConfig(updates: Partial<PushNotificationConfig>): PushNotificationConfig {
  const settings = getPWASettings();
  const updated = { ...settings.pushConfig, ...updates };
  updatePWASettings({ pushConfig: updated });
  return updated;
}

export function updateNotificationTopic(topicId: string, enabled: boolean): void {
  const settings = getPWASettings();
  const topics = settings.pushConfig.topics.map((topic) =>
    topic.id === topicId ? { ...topic, enabled } : topic
  );
  updatePushConfig({ topics });
}

export function getPWAAnalytics(): PWAAnalytics {
  return storage.get<PWAAnalytics>(STORAGE_KEYS.PWA_ANALYTICS) || { ...DEFAULT_ANALYTICS };
}

export function resetPWASettings(): PWASettings {
  storage.set(STORAGE_KEYS.PWA_SETTINGS, DEFAULT_SETTINGS);
  return DEFAULT_SETTINGS;
}

// ===========================================
// MANIFEST GENERATOR
// ===========================================

export function generateManifest(config: PWAManifestConfig): object {
  return {
    name: config.name,
    short_name: config.shortName,
    description: config.description,
    theme_color: config.themeColor,
    background_color: config.backgroundColor,
    display: config.display,
    orientation: config.orientation,
    start_url: config.startUrl,
    scope: config.scope,
    lang: config.lang,
    dir: config.dir,
    categories: config.categories,
    icons: config.icons.map((icon) => ({
      src: icon.src,
      sizes: icon.sizes,
      type: icon.type,
      ...(icon.purpose && { purpose: icon.purpose }),
    })),
    screenshots: config.screenshots.map((screenshot) => ({
      src: screenshot.src,
      sizes: screenshot.sizes,
      type: screenshot.type,
      ...(screenshot.label && { label: screenshot.label }),
      ...(screenshot.formFactor && { form_factor: screenshot.formFactor }),
    })),
    shortcuts: config.shortcuts.map((shortcut) => ({
      name: shortcut.name,
      short_name: shortcut.shortName,
      description: shortcut.description,
      url: shortcut.url,
      icons: shortcut.icons,
    })),
    related_applications: config.relatedApplications,
    prefer_related_applications: config.preferRelatedApplications,
  };
}

// ===========================================
// SPLASH SCREEN CONFIG
// ===========================================

export interface SplashScreenConfig {
  backgroundColor: string;
  iconPath: string;
  name: string;
}

export function generateAppleSplashScreens(): string[] {
  // Apple splash screen sizes for various devices
  const sizes = [
    { width: 2048, height: 2732, name: "12.9\" iPad Pro" },
    { width: 1668, height: 2388, name: "11\" iPad Pro" },
    { width: 1536, height: 2048, name: "10.5\" iPad" },
    { width: 1125, height: 2436, name: "iPhone X/XS/11 Pro" },
    { width: 1242, height: 2688, name: "iPhone XS Max/11 Pro Max" },
    { width: 828, height: 1792, name: "iPhone XR/11" },
    { width: 1170, height: 2532, name: "iPhone 12/13/14" },
    { width: 1284, height: 2778, name: "iPhone 12/13/14 Pro Max" },
  ];

  return sizes.map(
    (size) =>
      `<link rel="apple-touch-startup-image" media="(device-width: ${Math.floor(size.width / 3)}px) and (device-height: ${Math.floor(size.height / 3)}px) and (-webkit-device-pixel-ratio: 3)" href="/splash/splash-${size.width}x${size.height}.png" />`
  );
}

// ===========================================
// SINGLETON (lazy-loaded for SSR compatibility)
// ===========================================

let _pwaStateManager: PWAStateManager | null = null;

function getPWAStateManager(): PWAStateManager {
  if (typeof window === "undefined") {
    // Return a dummy manager for SSR
    return {
      getState: () => ({
        isInstallable: false,
        installState: "idle" as InstallState,
        isStandalone: false,
        notificationPermission: "denied" as NotificationPermission,
        isPushSubscribed: false,
        serviceWorkerStatus: "unsupported" as const,
        lastUpdateCheck: null,
        updateAvailable: false,
        isOnline: true,
      }),
      subscribe: () => () => {},
      promptInstall: async () => "unavailable" as const,
      requestNotificationPermission: async () => "denied" as NotificationPermission,
      subscribeToPush: async () => null,
      unsubscribeFromPush: async () => {},
      checkForUpdates: async () => false,
      applyUpdate: async () => {},
    } as unknown as PWAStateManager;
  }
  if (!_pwaStateManager) {
    _pwaStateManager = new PWAStateManager();
  }
  return _pwaStateManager;
}

export const pwaStateManager = {
  get instance() { return getPWAStateManager(); },
  getState: () => getPWAStateManager().getState(),
  subscribe: (listener: (state: PWAState) => void) => getPWAStateManager().subscribe(listener),
  promptInstall: () => getPWAStateManager().promptInstall(),
  requestNotificationPermission: () => getPWAStateManager().requestNotificationPermission(),
  subscribeToPush: (vapidKey: string) => getPWAStateManager().subscribeToPush(vapidKey),
  unsubscribeFromPush: () => getPWAStateManager().unsubscribeFromPush(),
  checkForUpdates: () => getPWAStateManager().checkForUpdates(),
  applyUpdate: () => getPWAStateManager().applyUpdate(),
};

// ===========================================
// REACT HOOKS
// ===========================================

export function usePWAState(): PWAState {
  const [state, setState] = React.useState<PWAState>(pwaStateManager.getState());

  React.useEffect(() => {
    return pwaStateManager.subscribe(setState);
  }, []);

  return state;
}

export function usePWAInstall(): {
  isInstallable: boolean;
  installState: InstallState;
  promptInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
  isStandalone: boolean;
} {
  const state = usePWAState();

  return {
    isInstallable: state.isInstallable,
    installState: state.installState,
    promptInstall: () => pwaStateManager.promptInstall(),
    isStandalone: state.isStandalone,
  };
}

export function usePushNotifications(): {
  permission: NotificationPermission;
  isSubscribed: boolean;
  requestPermission: () => Promise<NotificationPermission>;
  subscribe: (vapidKey: string) => Promise<PushSubscription | null>;
  unsubscribe: () => Promise<boolean>;
} {
  const state = usePWAState();

  return {
    permission: state.notificationPermission,
    isSubscribed: state.isPushSubscribed,
    requestPermission: () => pwaStateManager.requestNotificationPermission(),
    subscribe: (vapidKey: string) => pwaStateManager.subscribeToPush(vapidKey),
    unsubscribe: () => pwaStateManager.unsubscribeFromPush(),
  };
}

export function useServiceWorker(): {
  status: PWAState["serviceWorkerStatus"];
  updateAvailable: boolean;
  checkForUpdates: () => Promise<boolean>;
  applyUpdate: () => Promise<void>;
} {
  const state = usePWAState();

  return {
    status: state.serviceWorkerStatus,
    updateAvailable: state.updateAvailable,
    checkForUpdates: () => pwaStateManager.checkForUpdates(),
    applyUpdate: () => pwaStateManager.applyUpdate(),
  };
}

export function usePWASettings(): {
  settings: PWASettings;
  updateSettings: typeof updatePWASettings;
  updateManifest: typeof updateManifestConfig;
  updatePush: typeof updatePushConfig;
  updateTopic: typeof updateNotificationTopic;
  reset: typeof resetPWASettings;
} {
  const [settings, setSettings] = React.useState<PWASettings>(getPWASettings());

  const updateAndRefresh = React.useCallback((updates: Partial<PWASettings>) => {
    const updated = updatePWASettings(updates);
    setSettings(updated);
    return updated;
  }, []);

  const updateManifestAndRefresh = React.useCallback((updates: Partial<PWAManifestConfig>) => {
    const updated = updateManifestConfig(updates);
    setSettings(getPWASettings());
    return updated;
  }, []);

  const updatePushAndRefresh = React.useCallback((updates: Partial<PushNotificationConfig>) => {
    const updated = updatePushConfig(updates);
    setSettings(getPWASettings());
    return updated;
  }, []);

  const updateTopicAndRefresh = React.useCallback((topicId: string, enabled: boolean) => {
    updateNotificationTopic(topicId, enabled);
    setSettings(getPWASettings());
  }, []);

  const resetAndRefresh = React.useCallback(() => {
    const settings = resetPWASettings();
    setSettings(settings);
    return settings;
  }, []);

  return {
    settings,
    updateSettings: updateAndRefresh,
    updateManifest: updateManifestAndRefresh,
    updatePush: updatePushAndRefresh,
    updateTopic: updateTopicAndRefresh,
    reset: resetAndRefresh,
  };
}

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

export function canShowInstallPrompt(): boolean {
  const settings = getPWASettings();
  if (!settings.installPromptEnabled) return false;

  const dismissedAt = storage.get<string>(STORAGE_KEYS.INSTALL_DISMISSED_AT);
  if (!dismissedAt) return true;

  const daysSinceDismissed = (Date.now() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
  return daysSinceDismissed >= settings.installPromptFrequency;
}

export function isInQuietHours(): boolean {
  const settings = getPWASettings();
  if (!settings.pushConfig.quietHoursEnabled) return false;

  const now = new Date();
  const currentTime = now.getHours() * 60 + now.getMinutes();
  
  const [startHour, startMin] = settings.pushConfig.quietHoursStart.split(":").map(Number);
  const [endHour, endMin] = settings.pushConfig.quietHoursEnd.split(":").map(Number);
  
  const startTime = startHour * 60 + startMin;
  const endTime = endHour * 60 + endMin;

  if (startTime < endTime) {
    return currentTime >= startTime && currentTime < endTime;
  } else {
    // Quiet hours span midnight
    return currentTime >= startTime || currentTime < endTime;
  }
}

export function detectDeviceType(): "mobile" | "tablet" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  
  const width = window.innerWidth;
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function getBrowserInfo(): { name: string; supportsInstall: boolean; supportsPush: boolean } {
  if (typeof window === "undefined") {
    return { name: "unknown", supportsInstall: false, supportsPush: false };
  }

  const ua = navigator.userAgent.toLowerCase();
  
  if (ua.includes("chrome") && !ua.includes("edg")) {
    return { name: "Chrome", supportsInstall: true, supportsPush: true };
  }
  if (ua.includes("edg")) {
    return { name: "Edge", supportsInstall: true, supportsPush: true };
  }
  if (ua.includes("firefox")) {
    return { name: "Firefox", supportsInstall: false, supportsPush: true };
  }
  if (ua.includes("safari") && !ua.includes("chrome")) {
    return { name: "Safari", supportsInstall: true, supportsPush: true };
  }
  if (ua.includes("opera")) {
    return { name: "Opera", supportsInstall: true, supportsPush: true };
  }
  
  return { name: "Other", supportsInstall: false, supportsPush: false };
}
